import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { extractContentFeatures } from '@atlassian/conversation-assistant';
import {
	ExperienceName as RovoExperienceName,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import {
	useEditorRovoBridge,
	type AddCommandHandlersFn,
	type AdditionalContext,
	type CreateMediaProviderFn,
	type StreamAbortCallback,
	type StreamCallback,
	type StreamEndCallback,
	type StreamErrorCallback,
	type StreamRepairCallback,
	type StreamStartCallback,
	type StreamTimeoutCallback,
	type UploadImageErrorCallback,
} from '@atlassian/editor-rovo-bridge';
import {
	ConfluenceDeepResearchPageCreateCommand,
	ConfluencePageInitCommand,
	ConfluenceSubmitPromptOnPageCreateCommand,
	PrepareContentForFinalizationCommand,
} from '@atlassian/rovo-content-bridge-api-commands';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store';

import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useLazyShowFeedbackFlag } from '@confluence/smart-create/entry-points/useLazyShowFeedbackFlag.ts';
import { FeedbackVariant } from '@confluence/smart-create/entry-points/FeedbackVariant.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { DeepResearchCommandHandler } from '../command-handlers-new/deep-research-command-handler.ts';
import { PageInitCommandHandler } from '../command-handlers-new/page-init-command-handler.ts';
import { PrepareContentForFinalizationCommandHandler } from '../command-handlers-new/prepare-content-for-finalization-command-handler.ts';
import { SubmitPromptCommandHandler } from '../command-handlers-new/submit-prompt-command-handler.ts';
import type { UseRovoBridgeCommandListenerParams } from '../RovoBridgeCommandListener.tsx';
import { log } from '../utils/aifc-logging.ts';
import { getDocument } from '../utils/bridge-document-utils.ts';
import { useIsPageViewMode } from '../utils/useIsPageViewMode.ts';

export const useEditorRovoBridgeCommandListener = ({
	contentId,
	contentLocation,
	editorAPI,
	setEditorDisabled,
	setTitlePlaceholderState,
	onTitleChange,
	mediaProvider,
	fakeGenerateAIHeaderImage,
	generateAIHeaderImage,
	forceDraftSync,
	flags,
	useGenericEditorSkill,
	onStreamEnd: onStreamEndCallback,
	additionalContext: additionalContextFromProps,
}: UseRovoBridgeCommandListenerParams & WithFlagsProps) => {
	const contentType = 'page';
	const { sendAnalyticsEvent } = useAnalytics();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isViewMode = useIsPageViewMode();
	const { isDraftLockedForEditing } = useApprovals();
	const isViewModeForRovo = isViewMode || isDraftLockedForEditing;
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { cloudId } = useSessionData();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { stagingAreaMode } = useStagingAreaState();

	const createMediaProvider: CreateMediaProviderFn = useCallback(async ({ contentId }) => {
		return createRendererMediaProvider({
			contentId,
			isExternalShare: false,
		});
	}, []);

	const onUploadImageError: UploadImageErrorCallback = useCallback(({ error }) => {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.PAGE_MODES,
		});
	}, []);

	const sendInitialCreationAnalytics = useCallback(async () => {
		try {
			let features;
			if (editorAPI) {
				const doc = await getDocument(editorAPI);
				if (doc) {
					features = extractContentFeatures(doc);
				}
			}

			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'smartCreateContent',
				action: 'initialContentCreated',
				attributes: {
					createdContentProduct: 'confluence',
					createdContentType: contentType,
					createdContentFeatures: features,
					createdContentMetadata: {
						contentId,
					},
				},
			});
		} catch (error) {
			getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
		}
	}, [editorAPI, contentId, contentType, sendAnalyticsEvent]);

	// Called once when streaming starts
	const onStreamStart: StreamStartCallback = useCallback(
		({ streamType }) => {
			setEditorDisabled?.(true);

			if (streamType === 'create' || streamType === 'deep-research-create') {
				setTitlePlaceholderState?.(true);
				if (isExperimentEnabled('confluence_aifc_image_generation_experiment')) {
					if (!fg('aifc_header_image_pseudo_title_enabled')) {
						void fakeGenerateAIHeaderImage?.();
					}
				}
			}

			// Succeed the AIFC_INITIAL_DATA_RECEIVED experience since content has started streaming.
			// This ensures this experience won't timeout and then fail the AIFC_GENERATE_CONTENT or AIFC_UPDATE_CONTENT experience.
			// NOTE: This experience isn't started for all flows, it'll be a no-op if it's not started.
			void sendAIFCExperienceTrackingToParent({
				status: 'success',
				actionType: contentType,
				actionId: contentId,
				contentContext: contentLocation,
				experienceName: RovoExperienceName.AIFC_INITIAL_DATA_RECEIVED,
			});
		},
		[
			setEditorDisabled,
			fakeGenerateAIHeaderImage,
			setTitlePlaceholderState,
			contentId,
			contentType,
			contentLocation,
		],
	);

	// Called on every stream chunk
	const onStream: StreamCallback = useCallback(
		({ payload }) => {
			if ('title' in payload.data) {
				const title = payload?.data?.title;
				onTitleChange?.(title);
				if (title) {
					setTitlePlaceholderState?.(false);
				}
			}
		},
		[onTitleChange, setTitlePlaceholderState],
	);

	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	// Called when streaming ends
	const onStreamEnd: StreamEndCallback = useCallback(
		async ({ streamType, payload }) => {
			try {
				// reuploadImages is handled in platform

				if (streamType === 'create') {
					const title = 'title' in payload?.data ? payload.data.title : undefined;

					if (title) {
						if (isExperimentEnabled('confluence_aifc_image_generation_experiment')) {
							if (!fg('aifc_header_image_pseudo_title_enabled')) {
								void generateAIHeaderImage?.({ adfContent: title });
							}
						}
					}
				}

				// This experience success is a no-op if AIFC_ALL_DATA_HANDLED is not started. It may not be started for all flows.
				void sendAIFCExperienceTrackingToParent({
					status: 'success',
					actionType: contentType,
					actionId: contentId,
					contentContext: contentLocation,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				});

				if (streamType === 'create' || streamType === 'deep-research-create') {
					void sendInitialCreationAnalytics();
				} else if (streamType === 'update') {
					if (!showSmartCreationModal && stagingAreaMode !== 'modal') {
						const traceId = typeof payload?.traceId === 'string' ? payload.traceId : undefined;
						const requestId =
							typeof payload?.requestId === 'string' ? payload.requestId : undefined;
						void showCreateWithRovoModalCloseFeedbackFlag({
							feedbackVariant: FeedbackVariant.AFTER_UPDATE_STREAM_COMPLETE,
							contentType,
							cloudId,
							traceId,
							requestId,
						});
					}
				}
			} finally {
				setEditorDisabled?.(false);
			}

			onStreamEndCallback?.();
		},
		[
			contentId,
			contentType,
			contentLocation,
			setEditorDisabled,
			generateAIHeaderImage,
			sendInitialCreationAnalytics,
			showCreateWithRovoModalCloseFeedbackFlag,
			showSmartCreationModal,
			stagingAreaMode,
			cloudId,
			onStreamEndCallback,
		],
	);

	const onStreamAbort: StreamAbortCallback = useCallback(() => {
		setEditorDisabled?.(false);

		sendAnalyticsEvent({
			eventType: 'operational',
			action: 'aborted',
			actionSubject: 'smartCreateContent',
			attributes: {
				contentType,
				contentId,
			},
		});
	}, [setEditorDisabled, contentId, contentType, sendAnalyticsEvent]);

	const onStreamTimeout: StreamTimeoutCallback = useCallback(
		({ streamType, traceId }) => {
			setEditorDisabled?.(false);

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'timeout',
				actionSubject: 'smartCreateContent',
				attributes: {
					contentType,
					contentId,
					streamType,
					traceId,
				},
			});
		},
		[setEditorDisabled, contentId, contentType, sendAnalyticsEvent],
	);

	const onStreamError: StreamErrorCallback = useCallback(
		({ error, streamType, traceId }) => {
			log('Error in command handler:', error);

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'error',
				actionSubject: 'smartCreateContent',
				attributes: {
					contentType,
					error: error instanceof Error ? error.message : String(error),
					stack: error instanceof Error && error.stack ? error.stack : undefined,
					streamType,
					traceId,
					contentId,
				},
			});

			void sendAIFCExperienceTrackingToParent({
				status: 'fail',
				actionType: contentType,
				actionId: contentId,
				contentContext: contentLocation,
				experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				error: error instanceof Error ? error.message : String(error),
			});

			if (fg('aifc_create_enabled')) {
				setEditorDisabled?.(false);
			}
		},
		[contentId, contentType, contentLocation, sendAnalyticsEvent, setEditorDisabled],
	);

	const onStreamRepair: StreamRepairCallback = useCallback(
		({ streamType, repairs }) => {
			sendAnalyticsEvent({
				eventType: 'operational',
				actionSubject: 'smartCreateContent',
				action: 'adfRepaired',
				attributes: {
					contentType,
					contentId,
					streamType,
					repairCount: repairs.length,
					// Manually selecting properties to avoid sending UGC
					// in case more properties are added in the future.
					repairs: repairs.map((repair) => ({
						type: repair.type,
						from: repair.from,
						to: repair.to,
						context: repair.context,
						expansion: repair.expansion,
					})),
				},
			});
		},
		[contentId, contentType, sendAnalyticsEvent],
	);

	const additionalContext = useMemo<AdditionalContext>(() => {
		return async () => {
			const more =
				typeof additionalContextFromProps === 'function'
					? await additionalContextFromProps()
					: additionalContextFromProps;
			return {
				...more,
				isViewMode: isViewModeForRovo,
			};
		};
	}, [isViewModeForRovo, additionalContextFromProps]);

	const addCommandHandlers: AddCommandHandlersFn = useCallback(
		({ commandManager }) => {
			commandManager
				.addDefaultCommandHandlers()
				.addCommandHandler(
					ConfluenceDeepResearchPageCreateCommand,
					new DeepResearchCommandHandler(commandManager.commandProps),
				)
				.addCommandHandler(
					ConfluencePageInitCommand,
					new PageInitCommandHandler(commandManager.commandProps),
				)
				.addCommandHandler(
					PrepareContentForFinalizationCommand,
					new PrepareContentForFinalizationCommandHandler(commandManager.commandProps, {
						forceDraftSync,
					}),
				)
				.addCommandHandler(
					ConfluenceSubmitPromptOnPageCreateCommand,
					new SubmitPromptCommandHandler(commandManager.commandProps, { generateAIHeaderImage }),
				);
		},
		[forceDraftSync, generateAIHeaderImage],
	);

	useEditorRovoBridge({
		contentLocation,
		contentId,
		editorAPI,
		addCommandHandlers,
		additionalContext,
		mediaProvider,
		createMediaProvider,
		onUploadImageError,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		useGenericEditorSkill,
	});
};
